import { motion } from 'framer-motion';
import { Check, Star, Zap, Crown, Shield, ArrowRight, Users, Target } from 'lucide-react';

const InsurancePackages = () => {
  const packages = [
    {
      name: "Essential Partnership",
      subtitle: "Perfect for growing insurance companies",
      price: "Starting at AED 150K",
      duration: "per year",
      color: "#90E0EF",
      icon: Shield,
      popular: false,
      description: "Core risk management and fraud detection capabilities to get you started with measurable results.",
      features: [
        "Basic fraud detection algorithms",
        "Claims processing automation",
        "Risk assessment dashboards",
        "Member portal access",
        "Email and chat support",
        "Quarterly business reviews",
        "Standard integration support",
        "Basic reporting and analytics",
        "Up to 50,000 members covered",
        "30-day implementation timeline"
      ],
      highlights: [
        "25% reduction in fraudulent claims",
        "40% faster claims processing",
        "24/7 platform availability"
      ],
      cta: "Start Essential Partnership"
    },
    {
      name: "Strategic Partnership",
      subtitle: "Most popular choice for established insurers",
      price: "Starting at AED 300K",
      duration: "per year",
      color: "#0077B6",
      icon: Target,
      popular: true,
      description: "Advanced AI-driven capabilities with predictive analytics and comprehensive risk management solutions.",
      features: [
        "Advanced AI fraud detection with machine learning",
        "Predictive risk analytics and modeling",
        "Automated pre-authorization workflows",
        "Custom member engagement programs",
        "Priority phone and email support",
        "Monthly strategic consultations",
        "Advanced integration with existing systems",
        "Real-time analytics and custom reporting",
        "Up to 200,000 members covered",
        "15-day implementation timeline",
        "Dedicated account manager",
        "Custom workflow optimization"
      ],
      highlights: [
        "45% reduction in total claims costs",
        "70% faster processing times",
        "98.5% fraud detection accuracy"
      ],
      cta: "Choose Strategic Partnership"
    },
    {
      name: "Enterprise Partnership",
      subtitle: "For market-leading insurance organizations",
      price: "Custom Pricing",
      duration: "tailored solution",
      color: "#03045E",
      icon: Crown,
      popular: false,
      description: "White-glove partnership with full customization, dedicated team, and market leadership positioning.",
      features: [
        "Fully customized AI models and algorithms",
        "Enterprise-grade predictive analytics suite",
        "Complete workflow automation and optimization",
        "White-label member and provider portals",
        "24/7 dedicated technical support team",
        "Weekly strategic planning sessions",
        "Full system integration and API access",
        "Advanced reporting with business intelligence",
        "Unlimited members and scalability",
        "5-day rapid deployment option",
        "Dedicated customer success team",
        "Industry benchmarking and competitive analysis",
        "Custom feature development",
        "Regulatory compliance assistance"
      ],
      highlights: [
        "60% reduction in operational costs",
        "Market leadership positioning",
        "Custom innovation roadmap"
      ],
      cta: "Discuss Enterprise Partnership"
    }
  ];

  const addOns = [
    {
      name: "Advanced Analytics Suite",
      description: "Enhanced reporting and business intelligence tools",
      price: "AED 25K/year"
    },
    {
      name: "Extended Member Coverage",
      description: "Support for additional members beyond base package",
      price: "AED 2/member/month"
    },
    {
      name: "Priority Implementation",
      description: "Accelerated deployment with dedicated team",
      price: "AED 50K one-time"
    },
    {
      name: "Custom Integration Services", 
      description: "Specialized integration with legacy systems",
      price: "AED 75K one-time"
    }
  ];

  const testimonials = [
    {
      quote: "The Strategic Partnership transformed our claims processing. We're seeing 45% cost reduction and our members love the improved experience.",
      author: "Dr. Ahmed Al-Rashid",
      position: "Chief Medical Officer",
      company: "Dubai Health Insurance",
      package: "Strategic Partnership"
    },
    {
      quote: "HIERSTO's Enterprise solution gave us the competitive edge we needed. We're now the market leader in preventive care programs.",
      author: "Sarah Al-Mahmoud",
      position: "CEO",
      company: "Gulf Insurance Group",
      package: "Enterprise Partnership"
    }
  ];

  return (
    <section id="packages" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center px-4 py-2 bg-[#90E0EF]/20 border border-[#90E0EF]/30 rounded-full mb-6">
            <Star className="w-4 h-4 text-[#03045E] mr-2" />
            <span className="text-[#03045E] font-semibold text-sm">Flexible Partnership Packages</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-[#03045E] mb-6">
            Choose Your <span className="text-[#0077B6]">Partnership Level</span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            From essential risk management to enterprise-level transformation, we have a partnership package 
            designed to meet your specific needs and deliver measurable ROI.
          </p>
        </motion.div>

        {/* Package Cards */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {packages.map((pkg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              viewport={{ once: true }}
              className={`relative bg-white rounded-3xl shadow-lg border-2 transition-all duration-300 hover:shadow-2xl ${
                pkg.popular 
                  ? 'border-[#0077B6] transform hover:scale-105' 
                  : 'border-gray-100 hover:border-[#90E0EF]/50'
              }`}
            >
              {/* Popular Badge */}
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-[#0077B6] text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                    Most Popular
                  </div>
                </div>
              )}

              <div className="p-8">
                {/* Package Header */}
                <div className="text-center mb-8">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: `${pkg.color}20` }}
                  >
                    <pkg.icon 
                      className="w-8 h-8" 
                      style={{ color: pkg.color }}
                    />
                  </div>
                  
                  <h3 className="text-2xl font-bold text-[#03045E] mb-2">{pkg.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{pkg.subtitle}</p>
                  
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-[#03045E]">{pkg.price}</span>
                    <span className="text-gray-500 text-sm ml-2">{pkg.duration}</span>
                  </div>
                  
                  <p className="text-gray-600 text-sm leading-relaxed">{pkg.description}</p>
                </div>

                {/* Key Highlights */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-[#03045E] mb-3">Key Benefits:</h4>
                  <div className="space-y-2">
                    {pkg.highlights.map((highlight, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <Star className="w-4 h-4 text-[#0077B6] flex-shrink-0" />
                        <span className="text-sm text-gray-700">{highlight}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Features List */}
                <div className="mb-8">
                  <h4 className="text-sm font-semibold text-[#03045E] mb-4">What's Included:</h4>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {pkg.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg ${
                    pkg.popular
                      ? 'bg-[#0077B6] text-white hover:bg-[#0077B6]/90'
                      : 'bg-[#03045E] text-white hover:bg-[#03045E]/90'
                  }`}
                >
                  {pkg.cta}
                </motion.button>
              </div>

              {/* Decorative Elements */}
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                viewport={{ once: true }}
                className="absolute -bottom-2 -right-2 w-6 h-6 rounded-full opacity-20"
                style={{ backgroundColor: pkg.color }}
              />
            </motion.div>
          ))}
        </div>

        {/* Add-ons Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-[#03045E] mb-4">
              Optional Add-ons & Extensions
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Enhance your partnership with additional services and capabilities tailored to your specific requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {addOns.map((addon, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl hover:border-[#90E0EF]/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-[#03045E] mb-2">{addon.name}</h4>
                    <p className="text-gray-600 text-sm">{addon.description}</p>
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-lg font-bold text-[#0077B6]">{addon.price}</div>
                    <Zap className="w-4 h-4 text-[#90E0EF] mx-auto mt-1" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-[#03045E] mb-4">
              What Our Partners Say
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-[#CAF0F8]/20 to-[#90E0EF]/20 rounded-2xl p-8 border border-[#90E0EF]/30"
              >
                <div className="mb-6">
                  <div className="flex items-center space-x-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <p className="text-gray-700 leading-relaxed italic">"{testimonial.quote}"</p>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-[#03045E]">{testimonial.author}</div>
                    <div className="text-sm text-gray-600">{testimonial.position}</div>
                    <div className="text-sm text-[#0077B6] font-semibold">{testimonial.company}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-gray-500">Partnership:</div>
                    <div className="text-sm font-semibold text-[#0077B6]">{testimonial.package}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-white rounded-3xl p-8 shadow-lg border border-[#90E0EF]/30">
            <h3 className="text-2xl md:text-3xl font-bold text-[#03045E] mb-4">
              Need a custom solution for your organization?
            </h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Our team can create a tailored partnership package that perfectly fits your specific requirements, 
              budget, and strategic objectives.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                onClick={() => {
                  const element = document.getElementById('features');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#03045E] text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-[#03045E]/90 transition-all duration-300 shadow-lg inline-flex items-center justify-center space-x-2"
              >
                <span>Explore All Features</span>
                <ArrowRight className="w-5 h-5" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-transparent border-2 border-[#0077B6] text-[#0077B6] px-8 py-4 rounded-xl font-semibold text-lg hover:bg-[#0077B6] hover:text-white transition-all duration-300 inline-flex items-center justify-center space-x-2"
              >
                <Users className="w-5 h-5" />
                <span>Request Custom Quote</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InsurancePackages;
